'use client';
import Bubble from './Bubble';
import Transcripts from './Transcripts';
import Controls from './Controls';
import type { PresentationProps } from '@/embed/render/adapter';
import type { PresentationEvent } from '@/embed/postmessage/protocol';

interface Props {
  props: PresentationProps;
  emit: (e: PresentationEvent) => void;
}

// Color grading shared by the idle background image and the live avatar
// video layer, so the two read as one consistent image when they crossfade
// (ported from the hosted site's `--theme-video-brightness`/`-saturate`
// tokens — see `hosted-sites/templates/starter/src/components/voice/BackgroundLayer.tsx`).
const AVATAR_FILTER = 'brightness(var(--mobeus-avatar-brightness, 0.72)) saturate(var(--mobeus-avatar-saturate, 0.9))';

// Every themable element carries a stable `className` (targetable from
// injected `styling.css` inside the shadow root) and a matching `part`
// (targetable from the host page via `::part(...)`). Colours come from
// `--mobeus-*` tokens with light-value fallbacks so iframe mode — which does
// not inject styles.css — renders exactly as before.
export default function ChatPanel({ props, emit }: Props) {
  if (!props.isOverlayExpanded) {
    return <Bubble onClick={() => emit({ type: 'toggleExpand' })} agentState={props.agentState} loading={props.isConnecting} />;
  }

  // Idle-state background: the site's configured avatar thumbnail if set,
  // else a themable gradient placeholder (no bundled default image asset
  // exists in src/embed/public today — see PRD Phase 2).
  const idleBackground = props.avatarThumbnailUrl
    ? `url(${props.avatarThumbnailUrl}) center / cover no-repeat`
    : 'radial-gradient(circle at 75% 15%, var(--mobeus-primary, #3b82f6) 0%, var(--mobeus-surface-muted, #f3f4f6) 65%)';

  // Only render the internal avatar mount slot when this embed instance is
  // actually wired for it (config.avatar === true + render:"shadow" — see
  // bootstrap.ts). The external `{mount: 'css-selector'}` form and iframe
  // mode never set `avatarInternalMount`, so they render no internal slot
  // here — avatar.ts attaches into the host-page-provided element instead.
  const showAvatarMount = props.avatarEnabled && props.avatarInternalMount;

  // Bubble↔panel swap transition (Phase 3): the panel `<div>` below is a
  // fresh DOM node every time `isOverlayExpanded` flips true (the swap above
  // is still a hard conditional return, per PRD Phase 3's "simplest correct
  // approach" — not a simultaneous-mount crossfade), so applying the
  // entrance class unconditionally here replays it on every open, in both
  // shadow and iframe render modes (this is content-level, independent of
  // `render/iframe.ts`'s outer iframe width/height transition).
  return (
    <div className="mobeus-panel mobeus-panel-enter" part="panel" style={{
      width: 400, height: 600,
      background: 'var(--mobeus-surface, #fff)',
      color: 'var(--mobeus-text, #111827)',
      borderRadius: 'var(--mobeus-radius, 12px)',
      boxShadow: 'var(--mobeus-shadow, 0 8px 24px rgba(0,0,0,0.18))',
      display: 'flex', flexDirection: 'column',
      fontFamily: 'var(--mobeus-font, system-ui, sans-serif)',
      position: 'relative', overflow: 'hidden',
    }}>
      <div className="mobeus-header" part="header" style={{
        padding: 12, borderBottom: '1px solid var(--mobeus-border, #e5e7eb)',
        display: 'flex', justifyContent: 'space-between',
      }}>
        <div>
          <div className="mobeus-title" part="title" style={{ fontWeight: 600, color: 'var(--mobeus-text, #111827)' }}>
            {props.agentName ?? 'Mobeus'}
          </div>
          <div className="mobeus-state" part="state" style={{ fontSize: 12, color: 'var(--mobeus-text-muted, #6b7280)' }}>
            {props.agentState}
          </div>
        </div>
        <button
          className="mobeus-close" part="close-button"
          onClick={() => emit({ type: 'toggleExpand' })}
          aria-label="Close"
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--mobeus-text-muted, #6b7280)' }}
        >x</button>
      </div>
      {props.error && (
        <div className="mobeus-error" part="error" style={{
          padding: '8px 12px',
          background: 'var(--mobeus-error-bg, #fef2f2)',
          color: 'var(--mobeus-error-text, #b91c1c)',
          fontSize: 12,
          borderBottom: '1px solid var(--mobeus-border, #e5e7eb)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
        }}>
          <span>{props.error.message}</span>
          <button
            onClick={() => emit({ type: 'connect' })}
            aria-label="Retry"
            style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}
          >Retry</button>
        </div>
      )}
      {/* Mic-permission notice — deliberately NOT styled like the error banner
        * above. `micWarning` fires on every connect attempt (see bootstrap.ts's
        * eager mic-enable call), including for text-only users who simply
        * decline the browser's permission prompt; it's not proof anything is
        * actually broken, and text chat keeps working regardless. */}
      {props.micWarning && (
        <div className="mobeus-mic-warning" part="mic-warning" style={{
          padding: '8px 12px',
          background: 'var(--mobeus-warn-bg, #fffbeb)',
          color: 'var(--mobeus-warn-text, #92400e)',
          fontSize: 12,
          borderBottom: '1px solid var(--mobeus-border, #e5e7eb)',
        }}>
          Microphone access wasn&apos;t granted — you can still type below.
        </div>
      )}
      <div className="mobeus-content" part="content" style={{
        position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Idle background image/gradient — always present behind the
          * transcript area in the expanded panel; fades down once the live
          * avatar video is showing (see `showAvatarMount` below), letting it
          * read through as a base layer during the crossfade. */}
        {/* The pulse class animates opacity, and CSS animations override the
          * inline opacity below — so it must never be applied to a layer
          * that's currently supposed to be hidden, or it ghosts back in. */}
        <div
          className={`mobeus-avatar-idle${props.agentState === 'thinking' && !(showAvatarMount && props.avatarVisible) ? ' mobeus-thinking-pulse' : ''}`}
          part="avatar-idle"
          style={{
            position: 'absolute', inset: 0, zIndex: 0,
            background: idleBackground,
            opacity: showAvatarMount && props.avatarVisible ? 0 : props.isConnected ? 1 : 0.8,
            filter: AVATAR_FILTER,
            transition: 'opacity 0.6s ease, filter 0.6s ease',
            pointerEvents: 'none',
          }}
        />
        {/* Internal avatar video mount. `installAvatarBuiltin` (via
          * `RenderAdapter.getAvatarMountEl()`) finds this node by its
          * `data-mobeus-avatar-mount` attribute and attaches the LiveKit
          * video track's <video> element into it directly — this component
          * never touches the track itself, only provides the DOM slot and
          * the `opacity`/`filter` crossfade below (avatar.ts is render-mode-
          * agnostic and doesn't apply Mobeus-specific grading, since it's
          * also used for external host-page mounts we don't want to force
          * styling onto). */}
        {showAvatarMount && (
          <div
            data-mobeus-avatar-mount
            className={`mobeus-avatar-video${props.agentState === 'thinking' && props.avatarVisible ? ' mobeus-thinking-pulse' : ''}`}
            part="avatar-video"
            style={{
              position: 'absolute', inset: 0, zIndex: 0,
              opacity: props.avatarVisible ? 1 : 0,
              filter: AVATAR_FILTER,
              transition: 'opacity 0.6s ease, filter 0.6s ease',
              pointerEvents: 'none',
            }}
          />
        )}
        <div className="mobeus-transcripts-wrap" style={{
          position: 'relative', zIndex: 1, flex: 1, minHeight: 0, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          <Transcripts
            transcripts={props.transcripts}
            toolActivity={props.toolActivity}
            showToolCalls={props.showToolCalls}
            citations={props.citations}
            showCitations={props.showCitations}
            avatarThumbnailUrl={props.avatarThumbnailUrl}
          />
        </div>
      </div>
      <Controls isMuted={props.isMuted} isConnected={props.isConnected} isConnecting={props.isConnecting} chatMode={props.chatMode} emit={emit} />
    </div>
  );
}
